import { IColumnSettings } from '../../../entities/IClusters';

type FormatMessage = (value: { id: string; defaultMessage?: string }) => string;

/** Sentinel so missing keys do not fall back to `global.defaultMessage` / the raw id. */
const MISSING_TRANSLATION = '\u0000';

export const CUSTOM_PROPERTY_FIELD_PATTERN = /^p([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{11,12})$/i;

const formatIfExists = (formatMessage: FormatMessage, id?: string): string | undefined => {
    if (!id) {
        return undefined;
    }
    const result = formatMessage({ id, defaultMessage: MISSING_TRANSLATION });
    if (!result || result === MISSING_TRANSLATION) {
        return undefined;
    }
    return result;
};

const shouldPreferWorkspaceFieldLabel = (field?: string): boolean =>
    !!field && (CUSTOM_PROPERTY_FIELD_PATTERN.test(field) || field.includes('-'));

/** Shared cluster catalog keys that steal Status / Created at labels from other tables. */
const SHARED_CLUSTER_FILTER_FIELDS = new Set([
    'status',
    'createdAt',
    'endedAt',
    'lastUpdatedAt',
    'creationDate'
]);

const resolveWorkspaceFieldLabel = (field: string | undefined, formatMessage: FormatMessage): string | undefined => {
    if (!field) {
        return undefined;
    }
    const fromCatalog = formatIfExists(formatMessage, `filters.map.${field}`)
        || formatIfExists(formatMessage, field);
    if (fromCatalog) {
        return fromCatalog;
    }
    if (!shouldPreferWorkspaceFieldLabel(field)) {
        return undefined;
    }
    const stakePrefixes = [
        'event-description',
        'eApproval-event-expenses-documents',
        'eApproval-sponsoring-documents',
        'eApproval-grouping-documents',
        'eApproval-free-declaration-documents'
    ];
    for (const stake of stakePrefixes) {
        const fromStake = formatIfExists(formatMessage, `StakeProperties.${stake}.${field}`);
        if (fromStake) {
            return fromStake;
        }
    }
    return undefined;
};

/** Header / filter / sort label: workspace Labels, then translationKey, then headerText. */
export const resolveColumnLabel = (
    col: IColumnSettings | undefined,
    formatMessage: FormatMessage
): string | undefined => {
    if (!col) {
        return undefined;
    }
    if (shouldPreferWorkspaceFieldLabel(col.field)) {
        const fromWorkspace = resolveWorkspaceFieldLabel(col.field, formatMessage);
        if (fromWorkspace) {
            return fromWorkspace;
        }
    }
    if (col.translationKey) {
        return formatIfExists(formatMessage, col.translationKey) || col.headerText?.trim() || undefined;
    }
    if (SHARED_CLUSTER_FILTER_FIELDS.has(col.field) && col.headerText?.trim()) {
        return col.headerText;
    }
    const fromCatalog = resolveWorkspaceFieldLabel(col.field, formatMessage);
    if (fromCatalog) {
        return fromCatalog;
    }
    if (col.headerText?.trim()) {
        return col.headerText;
    }
    return undefined;
};

export const resolveDatagridFieldLabel = (
    field: string,
    columnsSettings: IColumnSettings[] | undefined,
    formatMessage: FormatMessage,
    propertyName?: string
): string => {
    const col = columnsSettings?.find(c => c.field === field);
    if (!col && !propertyName && field.includes('.')) {
        const lastDot = field.lastIndexOf('.');
        return resolveDatagridFieldLabel(field.slice(0, lastDot), columnsSettings, formatMessage, field.slice(lastDot + 1));
    }
    const columnLabel = resolveColumnLabel(col, formatMessage);
    const baseLabel = columnLabel || col?.headerText || field;

    if (propertyName) {
        if (col?.propertyLabels?.[propertyName]) {
            return col.propertyLabels[propertyName];
        }
        const defaultMessage = propertyName === 'currency'
            ? `${baseLabel} (${formatMessage({ id: 'datagrid.filter.currencySuffix', defaultMessage: 'currency' })})`
            : propertyName === 'number'
                ? baseLabel
                : `${baseLabel} (${propertyName})`;
        return formatIfExists(formatMessage, `filters.map.${field}.${propertyName}`) || defaultMessage;
    }
    if (columnLabel) {
        return columnLabel;
    }
    return formatIfExists(formatMessage, `filters.map.${field}`) || field;
};
